import dataAxios from '../../modules/auth/core/dataAxios'

export type ReviewStatus = 'pending' | 'approved' | 'rejected'
export type ReviewSource = 'internal' | 'google' | 'tripadvisor'

export interface TourReviewAdmin {
  _id: string
  tour?: {_id?: string; title?: string} | null
  user?: {_id?: string; firstName?: string; lastName?: string; email?: string} | null
  reservation?: Record<string, unknown> | null
  authorName: string
  authorAvatar?: string
  authorProfileUrl?: string
  rating: number
  title?: string
  comment: string
  language?: string
  source: ReviewSource
  externalId?: string
  externalUrl?: string
  status: ReviewStatus
  adminReply?: string
  adminReplyAt?: string
  reviewDate?: string
  createdAt?: string
  isFeatured?: boolean
  isActive?: boolean
}

export interface AdminListPagination {
  totalPages: number
  currentPage: number
  pageSize: number
  totalItems: number
}

export interface AdminListResponse {
  data: TourReviewAdmin[]
  payload: {
    pagination: AdminListPagination
  }
}

export interface ExternalReviewConfigRow {
  _id?: string
  provider: 'google' | 'tripadvisor'
  isEnabled: boolean
  apiKeyMasked: string
  placeId?: string
  locationId?: string
  linkedTourId?: string | null
  tripAdvisorLogoUrl?: string
  lastSyncAt?: string
  lastSyncStatus?: string
  lastSyncError?: string
  syncIntervalHours?: number
}

export async function fetchAdminReviews(params: {
  page?: number
  limit?: number
  status?: ReviewStatus | ''
  source?: ReviewSource | ''
  tour?: string
  rating?: number | ''
  search?: string
}): Promise<AdminListResponse> {
  const q: Record<string, string | number> = {
    page: params.page ?? 0,
    limit: params.limit ?? 15,
  }
  if (params.status) q.status = params.status
  if (params.source) q.source = params.source
  if (params.tour) q.tour = params.tour
  if (params.rating !== '' && params.rating != null) q.rating = Number(params.rating)
  if (params.search?.trim()) q.search = params.search.trim()

  const res = await dataAxios.get<AdminListResponse>('tour-reviews/admin', {params: q})
  return res.data
}

export async function fetchPendingReviewsCount(): Promise<number> {
  const res = await dataAxios.get<AdminListResponse>('tour-reviews/admin', {
    params: {status: 'pending', limit: 0, page: 0},
  })
  return res.data?.payload?.pagination?.totalItems ?? 0
}

export async function moderateReview(
  id: string,
  body: {status: 'approved' | 'rejected'; moderationNote?: string}
): Promise<void> {
  await dataAxios.patch(`tour-reviews/${id}/moderate`, body)
}

export async function replyToReview(id: string, adminReply: string): Promise<void> {
  await dataAxios.patch(`tour-reviews/${id}/reply`, {adminReply})
}

export async function toggleFeatured(id: string): Promise<void> {
  await dataAxios.patch(`tour-reviews/${id}/featured`)
}

export async function deleteReview(id: string): Promise<void> {
  await dataAxios.delete(`tour-reviews/${id}`)
}

export async function fetchExternalReviewConfigs(): Promise<ExternalReviewConfigRow[]> {
  const res = await dataAxios.get<{data?: ExternalReviewConfigRow[]}>(
    'external-review-config'
  )
  const d = res.data as {data?: ExternalReviewConfigRow[]} | ExternalReviewConfigRow[]
  if (Array.isArray(d)) return d
  return Array.isArray(d?.data) ? d.data : []
}

export async function updateExternalReviewConfig(
  provider: 'google' | 'tripadvisor',
  body: Record<string, unknown>
): Promise<ExternalReviewConfigRow> {
  const res = await dataAxios.put<{data: ExternalReviewConfigRow}>(
    `external-review-config/${provider}`,
    body
  )
  return res.data?.data as ExternalReviewConfigRow
}

export async function triggerExternalSync(
  provider: 'google' | 'tripadvisor'
): Promise<void> {
  await dataAxios.post(`external-review-config/${provider}/sync`)
}

export interface TourOption {
  _id: string
  label: string
}

export async function fetchToursForSelect(): Promise<TourOption[]> {
  const res = await dataAxios.get('tours', {params: {page: 0, size: 500}})
  const raw = res.data?.data ?? res.data
  const list = Array.isArray(raw) ? raw : []
  return list.map((t: any) => ({
    _id: t._id,
    label:
      t.translations?.[0]?.title ??
      t.title ??
      t.seoSlug ??
      String(t._id),
  }))
}
